import logger from '~/logger';
import { trackRaw } from '~/services/segment.service';
import env from '~/env.public';

const EVENT_SOURCE = 'web-v2';

/** Map of event name to the optional transformer function. */
export type TransformerMap<T> = { [Ev in keyof T]?: TransformFunc<T, Ev> };

/**
 * A "transformer" function that takes the metadata passed to `track` and
 * transforms it into the metadata shape that is sent to segment. Is null if
 * the event is not transformed.
 */
type TransformFunc<T, U extends keyof T> = (params: T[U]) => object;

export const trackWithCommonMetadata =
  <MetadataMap extends { [k: string]: object }>(
    commonMetadata: Array<Record<string, any>>,
    transformers: TransformerMap<MetadataMap>,
  ) =>
  <U extends keyof MetadataMap>(
    params: U extends string ? { type: U; metadata: MetadataMap[U] } : never,
  ) => {
    // Transform the metadata if a metadata transformer is defined
    const metadataTransformer = transformers[params.type];
    const metadata =
      metadataTransformer == null
        ? params.metadata
        : metadataTransformer(params.metadata);

    // Merge all the common metadata into a single object
    const sharedMetadata = commonMetadata.reduce<Record<string, any>>(
      (acc, el) => ({ ...acc, ...el }),
      {},
    );

    // Log the event
    const payload = {
      ...sharedMetadata,
      ...metadata,
      eventSource: EVENT_SOURCE,
    };
    if (
      ('userEmail' in payload &&
        typeof payload.userEmail === 'string' &&
        payload.userEmail.endsWith('@trucksmarter.com')) ||
      env.ENABLE_CONSOLE_LOGS
    ) {
      logger.info(params.type, payload);
    }
    trackRaw(params.type, payload);
  };
